/**
 * This Layout is needed for Starter Kit.
 */

// Global
import {
  LayoutServiceData,
  Field,
  HTMLLink,
  useSitecoreContext,
  ImageField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { GoogleAnalytics } from '@next/third-parties/google';
//import { tv } from 'tailwind-variants';

// Local
//import SkipNav from 'components/authorable/shared/site-structure/SkipNav/SkipNav';
import Scripts from 'src/Scripts';
import jssConfig from 'temp/config';
import { PlaceholderWrapper } from './helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
//import { SitecoreSearchWidgetsProviderWrapper } from 'widgets/WidgetProviderWrapper';

// Prefix public assets with a public URL to enable compatibility with Sitecore Experience Editor.
// If you're not supporting the Experience Editor, you can remove this.
const publicUrl = jssConfig.publicUrl;

// const TAILWIND_VARIANTS = tv({
//   slots: {
//     footer: ['bg-gray-200'],
//     footerContentContainer: [
//       'm-auto',
//       'max-w-7xl',
//       'p-4',
//       'mdlg:grid',
//       'mdlg:grid-cols-4',
//       'mdlg:items-center',
//       'mdlg:m-auto',
//     ],
//     mainContentContainer: ['mt-[90px]'],
//   },
// });

interface LayoutProps {
  layoutData: LayoutServiceData;
  headLinks: HTMLLink[] | null;
}

interface RouteFields {
  [key: string]: unknown;
  Title?: Field<string>;
  Content?: Field<string>;
  Description?: Field<string>;
  facebookTitle?: Field<string>;
  facebookDescription?: Field<string>;
  facebookImage?: ImageField;
  twitterTitle?: Field<string>;
  twitterDescription?: Field<string>;
  twitterImage?: ImageField;
  heroImage?: ImageField;
  previewText?: Field<string>;
  metaKeywords?: Field<string>;
  metaRobotsNOFOLLOW?: Field<boolean>;
  metaRobotsNOINDEX?: Field<boolean>;
}

import localFont from 'next/font/local';
import { TransformedSiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { normalizeImageUrl } from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import Alert from 'components/authorable/shared/content/Alert';
import SkipToMainContentBar from 'components/authorable/shared/site-structure/SkipToMainContentBar/SkipToMainContentBar';
import { MetaData } from 'helpers/MetaData';
import CleanUrlParams from 'lib/utils/urlCleaner';
import { BLOG_TEMPLATE_ID, PROVIDER_TEMPLATE_ID } from 'lib/constants';
import useDictionary from 'lib/hooks/useDictionary';

// Century Gothic Regular
export const centuryGothic = localFont({
  src: [
    {
      path: '../public/assets/fonts/century-gothic-regular.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: '../public/assets/fonts/century-gothic-regular-italic.woff2',
      weight: '400',
      style: 'italic',
    },
    {
      path: '../public/assets/fonts/century-gothic-bold.woff2',
      weight: '700',
      style: 'normal',
    },
    {
      path: '../public/assets/fonts/century-gothic-bold-italic.woff2',
      weight: '700',
      style: 'italic',
    },
  ],
  display: 'swap',
  variable: '--font-century-gothic',
});

// Nunito sans
export const nunitoSans = localFont({
  src: [
    {
      path: '../public/assets/fonts/nunitosans-variable.ttf',
      weight: '400',
      style: 'normal',
    },
    {
      path: '../public/assets/fonts/nunitosans-italic-variable.ttf',
      weight: '400',
      style: 'italic',
    },
    {
      path: '../public/assets/fonts/nunitosans-variable.ttf',
      weight: '700',
      style: 'normal',
    },
    {
      path: '../public/assets/fonts/nunitosans-italic-variable.ttf',
      weight: '700',
      style: 'italic',
    },
  ],
  display: 'swap',
  variable: '--font-nunito-sans',
});

function limitString(str: string, limit = 300): string {
  return str.length > limit ? str.slice(0, limit) + '...' : str;
}

// Function to strip HTML tags from text
function stripHtmlTags(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

// Return the first truthy string from an array of fields or undefined
function getFirstStringValue(valuesToCheck: (Field<string> | undefined)[]): string | undefined {
  const stringValue = valuesToCheck?.find((s) => s?.value);
  return stringValue?.value?.trim();
}

const Layout = ({ layoutData, headLinks }: LayoutProps): JSX.Element => {
  const {
    route,
    // context: { site },
  } = layoutData.sitecore;
  const {
    Title,
    Description,
    facebookTitle,
    facebookDescription,
    facebookImage,
    twitterTitle,
    twitterDescription,
    twitterImage,
    previewText,
    heroImage,
    metaKeywords,
    metaRobotsNOFOLLOW,
    metaRobotsNOINDEX,
  } = (route?.fields as RouteFields) ?? [];
  const { pageEditing } = layoutData.sitecore.context;
  const { siteSettings } = useSitecoreContext().sitecoreContext;
  //const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const currentUrl = (() => {
    const publicUrl = jssConfig.publicUrl;
    const itemPath = layoutData.sitecore.context.itemPath;

    if (publicUrl && itemPath) {
      return `${publicUrl}${itemPath}`.toLowerCase();
    }

    // JavaScript fallback
    if (typeof window !== 'undefined') {
      return window.location.href.toLowerCase();
    }

    return '';
  })();
  const mainClassPageEditing = pageEditing ? 'editing-mode' : 'prod-mode';
  const pageTitle = Title?.value?.toString();
  const { getDictionaryValue } = useDictionary();
  const { gtmID } = layoutData.sitecore?.context?.siteSettings ?? {};
  const googleTagManagerId = gtmID?.value ?? '';

  const faviconUrl =
    (siteSettings as TransformedSiteSettings)?.Favicon?.value?.src || `${publicUrl}/favicon.ico`;

  const pageMetaTitle =
    PROVIDER_TEMPLATE_ID === route?.templateId
      ? Title?.value?.toString()
      : (route?.fields?.pageMetaTitle as Field<string>)?.value
        ? (route?.fields?.pageMetaTitle as Field<string>)?.value?.toString()
        : Title?.value?.toString();

  const pageMetaDescription = getFirstStringValue([
    { value: (route?.fields?.metaDescription as Field<string>)?.value },
    Description ? { value: stripHtmlTags(Description.value || '') } : undefined,
  ]);

  const ogTitle = getFirstStringValue([facebookTitle, Title]);

  const ogDescription = getFirstStringValue([
    facebookDescription,
    pageMetaDescription ? { value: pageMetaDescription } : undefined,
  ]);

  const twitterTitleValue = getFirstStringValue([twitterTitle, Title]);

  const twitterDescriptionValue = getFirstStringValue([
    twitterDescription,
    pageMetaDescription ? { value: pageMetaDescription } : undefined,
  ]);

  const robots =
    metaRobotsNOFOLLOW?.value && metaRobotsNOINDEX?.value
      ? 'none'
      : metaRobotsNOFOLLOW?.value
        ? 'nofollow'
        : metaRobotsNOINDEX?.value
          ? 'noindex'
          : 'all';

  // hide sr-only h1 if there is another h1 is there - CW-1342
  useEffect(() => {
    const allH1s = document.querySelectorAll('h1');
    if (allH1s.length > 1) {
      allH1s.forEach((h1) => {
        if (h1.classList.contains('sr-only')) {
          h1.remove();
        }
      });
    }
  }, []);

  /*
   * RENDERING
   */

  // const { footer, footerContentContainer, mainContentContainer } = TAILWIND_VARIANTS();

  return (
    <>
      <Scripts />
      <MetaData layoutData={layoutData} />
      <Head>
        {/* {site?.name === 'childrens' && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: `
            !function (i, t, n, o, p, c) {
              i.epd || (((o = i.epd = function () {
                var t;
                i._epd ? (t = i._epd).call.apply(t, arguments) : o.q.push(arguments)
              }).push = o).q = [],
              (p = t.createElement(n)).async = !0,
              p.src = "https://js.eruptr.io/epd.js",
              (c = t.getElementsByTagName(n)[0]).parentNode.insertBefore(p, c))
            }(window, document, "script"),
            epd("initG", "5517668213");
          `,
              }}
            />

            <script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
                  window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
                  heap.load("3019744410");
                `,
              }}
            />
          </>
        )} */}
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=yes" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <title>{`${pageMetaTitle || 'Page'} ${getDictionaryValue('SiteTitle') && `| ${getDictionaryValue('SiteTitle')}`} `}</title>
        <meta property="fb:pages" content="251542931536626" />
        {currentUrl && <link rel="canonical" href={currentUrl} />}
        {currentUrl && <meta property="og:url" content={currentUrl} />}
        {BLOG_TEMPLATE_ID === route?.templateId ? (
          <>
            {Title?.value && <meta property="og:title" content={Title?.value?.toString()} />}
            {previewText?.value && (
              <meta
                property="og:description"
                content={limitString(previewText?.value?.toString())}
              />
            )}
            <meta property="og:type" content="article" />
            {heroImage?.value?.src && (
              <meta property="og:image" content={normalizeImageUrl(heroImage?.value?.src)} />
            )}
          </>
        ) : (
          <>
            {ogTitle && <meta property="og:title" content={ogTitle} />}
            {ogDescription && (
              <meta property="og:description" content={limitString(ogDescription)} />
            )}
            {facebookImage?.value?.src && (
              <meta property="og:image" content={normalizeImageUrl(facebookImage?.value?.src)} />
            )}
            <meta property="og:type" content="website" />
          </>
        )}

        <link href={normalizeImageUrl(faviconUrl)} rel="icon" type="image/x-icon" />
        <link rel="shortcut icon" href={normalizeImageUrl(faviconUrl)} type="image/x-icon" />

        <meta name="twitter:card" content="summary" />
        {currentUrl && <meta name="twitter:url" content={currentUrl} />}

        {twitterTitle && <meta name="twitter:title" content={twitterTitleValue} />}
        {twitterDescriptionValue && (
          <meta name="twitter:description" content={limitString(twitterDescriptionValue)} />
        )}
        {twitterImage?.value?.src && (
          <meta name="twitter:image" content={normalizeImageUrl(twitterImage?.value?.src)} />
        )}

        {pageMetaDescription && (
          <meta name="description" content={limitString(pageMetaDescription || '')} />
        )}
        <meta name="keywords" content={metaKeywords?.value?.toString()} />
        {robots && <meta name="robots" content={robots} />}

        {headLinks?.map((headLink) => (
          <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
        ))}
      </Head>
      {/* <SitecoreSearchWidgetsProviderWrapper> */}
      {/* <SkipNav /> */}
      {googleTagManagerId && <GoogleAnalytics gaId={googleTagManagerId} />}
      <div
        className={`${mainClassPageEditing} ${centuryGothic.variable} ${nunitoSans.variable} font-nunitoSans`}
      >
        <h1 className="sr-only">{pageTitle}</h1>
        <div className="w-full z-10" id="header-wrapper">
          {/* Alert */}
          <Alert type="warning" />
          <SkipToMainContentBar />
          <Alert type="neutral" />

          {/* Header */}
          {route && (
            <PlaceholderWrapper helpTextHideIf={true} name="headless-header" rendering={route} />
          )}
        </div>
        <main id="mainContent">
          <div id="content">
            {route && (
              <PlaceholderWrapper
                helpTextHideIf={true}
                name="headless-breadcrumb"
                rendering={route}
              />
            )}
            {route && (
              <PlaceholderWrapper helpTextHideIf={true} name="headless-main" rendering={route} />
            )}
          </div>
        </main>
        <footer>
          <div>
            {route && (
              <PlaceholderWrapper helpTextHideIf={true} name="headless-footer" rendering={route} />
            )}
          </div>
        </footer>
      </div>
      {/* </SitecoreSearchWidgetsProviderWrapper> */}
      <CleanUrlParams />
    </>
  );
};

export default Layout;
